import { zodResolver } from "@hookform/resolvers/zod"
import { useNavigate } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { UpsertKlaviyoIntegration } from "schema"
import { z } from "zod"
import { useCreateKlaviyoIntegrationConfigurationMutation } from "../../../hooks/klaviyo/useCreateKlaviyoIntegrationConfigurationMutation"
import { useKlaviyoIntegrationQuery } from "../../../hooks/klaviyo/useKlaviyoIntegrationQuery"

export const KlaviyoAuthModal = () => {
  const { t } = useTranslation()
  const form = useForm<z.infer<typeof UpsertKlaviyoIntegration>>({
    resolver: zodResolver(UpsertKlaviyoIntegration),
  })
  const navigate = useNavigate()
  const { mutateAsync } = useCreateKlaviyoIntegrationConfigurationMutation()
  const { data } = useKlaviyoIntegrationQuery()

  const onSubmit = async (data: z.infer<typeof UpsertKlaviyoIntegration>) => {
    await mutateAsync(data)
    onOpenChange()
  }

  const onOpenChange = (isOpen: boolean = false) => {
    if (!isOpen) {
      navigate({ to: "/settings/integrations/klaviyo", search: {} })
    }
  }

  useEffect(() => {
    if (data) {
      form.reset(data)
    }
  }, [data, form])

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("settings.integrations.klaviyo.authModal.title")}</DialogTitle>
          <DialogDescription>
            {t("settings.integrations.klaviyo.authModal.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          <Form
            {...form}
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
            errorTranslations={getErrorTranslations()}
          >
            <div className="flex flex-col w-full gap-8">
              <FormField
                control={form.control}
                name="apiKey"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("settings.integrations.klaviyo.authModal.form.apiKey")}
                    </FormLabel>
                    <FormControl>
                      <Input {...field} required />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <DialogFooter className="mt-8">
              <Button disabled={form.formState.isLoading || !form.formState.isValid}>
                {t("settings.integrations.klaviyo.authModal.form.submit")}
              </Button>
            </DialogFooter>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  )
}
